import {
	buildSyncUrl,
	getAuthenticatedRequestInit,
	getSyncHeaders,
} from './utils';
import { GraphicInterface } from '../stores/reducers/graphics';
import { Entity } from '../types/interfaces';
import { HttpRequest } from '../stores/types/types';
import { GeneratedContent } from '../stores/types/content';

export const addGraphics = (token: string, graphics: GraphicInterface, onSuccess: Function) => {
	const meetingId = graphics.meeting;
	// @ts-ignore
	return fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/graphics/${meetingId}`, {
		method: 'POST',
		body: JSON.stringify(graphics),
		...getAuthenticatedRequestInit(token),
	}).then(async (result) => {
		if (result.ok) {
			onSuccess(await result.json());
		} else {
			throw new Error('Unable to create graphics');
		}
	}).catch((error) => {
		throw error;
	});
};

export const deleteGraphics = (token: string, graphics: GraphicInterface, onSuccess: Function) => {
	const { id, meeting } = graphics;
	return fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/graphics/${id}/meeting/${meeting}`, {
		method: 'DELETE',
		...getAuthenticatedRequestInit(token),
	}).then((result) => {
		if (result.ok) {
			onSuccess();
		} else {
			throw new Error(`Could not delete graphics for id ${id}`);
		}
	});
};

export const loadGraphics = (token: string, meetingId: number) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/graphics/${meetingId}`, {
	method: 'GET',
	...getAuthenticatedRequestInit(token),
}).then((result) => {
	if (result.ok) {
		return result.json();
	}
	throw new Error(`Could not load graphics for meeting id ${meetingId}`);
});

export const bulkEditGraphics = (
	token: string,
	meetingId: number,
	graphics: GraphicInterface[],
	onSuccess: Function = () => {},
) =>
	// @ts-ignore
	// eslint-disable-next-line implicit-arrow-linebreak
	fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/graphics/bulk-edit/${meetingId}`, {
		method: 'POST',
		body: JSON.stringify(graphics),
		...getAuthenticatedRequestInit(token),
	}).then(async (result) => {
		if (result.ok) {
			onSuccess();
		} else {
			throw new Error('Unable to edit graphics');
		}
	}).catch((error) => {
		throw error;
	});
// eslint-disable-next-line camelcase
export const PollCloudConvert = (token: string, job_id: string) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/graphics/poll-cloud-convert/${job_id}`, {
	method: 'GET',
	...getAuthenticatedRequestInit(token),
// eslint-disable-next-line consistent-return
}).then(async (result) => {
	if (result.ok) {
		return result.json();
	}
}).catch((error) => {
	throw error;
});

export const getAllShotOverlays = async (
	token: string,
	roomId: number,
	getUpdated: boolean = false,
): Promise<Entity | null> => {
	try {
		const url = buildSyncUrl(
			`v1/overlays/enabled/${roomId}${getUpdated ? '?updated=true' : ''}`,
		);
		const res = await fetch(url, {
			method: HttpRequest.Get,
			headers: getSyncHeaders(token),
		});

		if (res.ok) {
			return await res.json();
		}
	} catch (error) {
		console.error(error);
	}

	return null;
};

export const updateOverlayInAllShotOverlays = async (
	token: string,
	roomId: number,
	overlayId: number,
	overlayEnabled: boolean,
): Promise<boolean> => {
	try {
		const url = buildSyncUrl(`v1/overlays/enabled/${roomId}/${overlayId}`);
		const res = await fetch(url, {
			method: HttpRequest.Put,
			headers: getSyncHeaders(token),
			body: JSON.stringify({ overlayEnabled }),
		});

		if (res.ok) {
			return true;
		}
	} catch (error) {
		console.error(error);
	}

	return false;
};

export const resetAllShotOverlays = async (
	token: string,
	roomId: number,
): Promise<boolean> => {
	try {
		const url = buildSyncUrl(`v1/overlays/reset-enabled/${roomId}`);
		const res = await fetch(url, {
			method: HttpRequest.Put,
			headers: getSyncHeaders(token),
		});

		if (res.ok) {
			return true;
		}
	} catch (error) {
		console.error(error);
	}

	return false;
};

export const getAllAnimatedOverlays = async (
	token: string,
	roomId: number,
): Promise<Entity | null> => {
	try {
		const url = buildSyncUrl(`v1/overlays/animated/${roomId}`);
		const res = await fetch(url, {
			method: HttpRequest.Get,
			headers: getSyncHeaders(token),
		});

		if (res.ok) {
			return await res.json();
		}
	} catch (error) {
		console.error(error);
	}

	return null;
};

export const updateOverlayInAllAnimatedOverlays = async (
	token: string,
	roomId: number,
	overlayId: number,
): Promise<boolean> => {
	try {
		const url = buildSyncUrl(`v1/overlays/animated/${roomId}/${overlayId}`);
		const res = await fetch(url, {
			method: HttpRequest.Put,
			headers: getSyncHeaders(token),
		});

		if (res.ok) {
			return true;
		}
	} catch (error) {
		console.error(error);
	}

	return false;
};

export const removeAnimatedOverlay = async (
	token: string,
	roomId: number,
	overlayId: number,
): Promise<boolean> => {
	try {
		const url = buildSyncUrl(`v1/overlays/animated/${roomId}/${overlayId}`);
		const res = await fetch(url, {
			method: HttpRequest.Delete,
			headers: getSyncHeaders(token),
		});

		if (res.ok) {
			return true;
		}
	} catch (error) {
		console.error(error);
	}

	return false;
};

export const removeAllAnimatedOverlays = async (
	token: string,
	roomId: number,
): Promise<boolean> => {
	try {
		const url = buildSyncUrl(`v1/overlays/all-animated/${roomId}`);
		const res = await fetch(url, {
			method: HttpRequest.Delete,
			headers: getSyncHeaders(token),
		});

		if (res.ok) {
			return true;
		}
	} catch (error) {
		console.error(error);
	}

	return false;
};

export const duplicateOverlayRequest = async (
	token: string,
	overlay: GraphicInterface,
	generatedContent?: GeneratedContent,
): Promise<boolean> => {
	if (!overlay.meeting || !overlay.id) {
		console.warn('Missing meeting or id for overlay: ', overlay.meeting, ' ', overlay.id);
		return false;
	}

	try {
		const url = buildSyncUrl(`v1/overlays/duplicate/${overlay.meeting}`);
		const res = await fetch(url, {
			method: HttpRequest.Put,
			headers: getSyncHeaders(token),
			body: JSON.stringify({
				overlay,
				generatedContent,
			}),
		});

		if (res.ok) {
			return true;
		}
	} catch (error) {
		console.error(error);
	}

	return false;
};

export const editOverlayDraft = async (
	token: string,
	overlay: GraphicInterface,
	// if the user is opening the modal, pass flag for backend
	isInit: boolean = false,
): Promise<boolean> => {
	if (!overlay.meeting || !overlay.id) {
		console.warn('Missing meeting or id for overlay: ', overlay.meeting, ' ', overlay.id);
		return false;
	}

	try {
		const url = buildSyncUrl(`v1/overlays/draft/${overlay.meeting}`);
		const res = await fetch(url, {
			method: HttpRequest.Put,
			headers: getSyncHeaders(token),
			body: JSON.stringify({ overlay, isInit }),
		});

		if (res.ok) {
			return true;
		}
	} catch (error) {
		console.error(error);
	}

	return false;
};
